import React, { useState, useEffect } from "react";
import EnterpriseDeletedAssessments from "./EnterpriseDeletedAssessments";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApi } from "../hooks/useApi";
import Card from "./Card";
import Button from "./Buttons";
import LoadingSpinner from "./LoadingSpinner";
import { FileText, Shield, Trash2, RefreshCw, Upload, Users, Eye } from "lucide-react";
import OrgAssignAssessModal from "./OrgAssignAssessModal";
import AssessmentRespondentsModal from "./AssessmentRespondentsModal";
import AssessmentUnassignedModal from "./AssessmentUnassignedModal";
import DeleteAssessmentModal from './DeleteAssessmentModal';
import ReassessModal from './ReassessModal';
import EnterpriseAssessmentHistoryMenu, { ReassessmentNote } from "./EnterpriseAssessmentHistoryMenu";
import AssessmentStatusBadge from "../utils/assessmentStatusLabel";
import { useRBAC } from "../hooks/useRBAC";
import AssessmentActivityLog from "./AssessmentActivityLog";
import { severityPostureScore } from "../utils/riskScoringEngine";

// The backend rolls each assessment's risks up to ACTIVE severity counts (excluding
// fully-remediated / closed), so the card no longer receives the individual risk array.
const computeRiskData = (rc) => {
  const riskCounts = {
    CRITICAL: rc?.critical || 0,
    HIGH: rc?.high || 0,
    MEDIUM: rc?.medium || 0,
    LOW: rc?.low || 0,
  };
  const riskScore = severityPostureScore(riskCounts);
  return { riskCounts, riskScore };
};

const EnterpriseOrganizationAssessmentCard = () => {
  const [orgData, setOrgData] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAssessmentForAssign, setSelectedAssessmentForAssign] = useState(null);
  const [respondentsAssessmentId, setRespondentsAssessmentId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [assessmentToReassess, setAssessmentToReassess] = useState(null);
  const [reassessing, setReassessing] = useState(false);
  const [reassessError, setReassessError] = useState(null);

  const [showUnassignedAlert, setShowUnassignedAlert] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useApi();
  const { user } = useAuth();
  const { canAssess, getAssessDisabledReason, canManageOrganizationRisks, canViewOrganizationAssessments, canAssessViaAssignment, canDeleteAssessments } = useRBAC();
  const canDeleteAssessment = canDeleteAssessments(); // super admin only
  const canManageRisks = canManageOrganizationRisks();

  // Bumped after each delete so an open "Deleted assessments" list reloads.
  const [deletedVersion, setDeletedVersion] = useState(0);

  const handleDeleteAssessment = async () => {
    if (!assessmentToDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiCall('DELETE', `/enterpriseAssessments/${assessmentToDelete}`);
      setShowDeleteModal(false);
      setAssessmentToDelete(null);
      setDeletedVersion((v) => v + 1);
      await fetchData();
    } catch (error) {
      setDeleteError(error?.response?.data?.message || 'Could not delete this assessment.');
    } finally {
      setDeleting(false);
    }
  };

  const handleReassessAssessment = async () => {
    if (!assessmentToReassess) return;
    const { id } = assessmentToReassess;
    setReassessing(true);
    setReassessError(null);
    try {
      const result = await apiCall('POST', `/enterpriseAssessments/${id}/reassess`);
      setShowReassessModal(false);
      setAssessmentToReassess(null);
      // Reassess creates a NEW assessment row — navigate to newAssessmentId, not the id that was
      // reassessed. The separate /analyze call this used to make for the document case is gone:
      // reassess enqueues that analysis itself, so by the time it would run the new row is already
      // Analyzing and it would just 409 against AnalyzeAssessment's own status guard.
      // Where to land is the server's call (`isDocumentAssessment`), not the row's document id: a
      // questionnaire assessment keeps a document id forever once an upload has been merged into
      // its scope, and reassessing one of those must still land on the answers to review rather
      // than on an analysis the user never asked for.
      const newId = result?.newAssessmentId ?? id;
      if (result?.isDocumentAssessment) {
        navigate('/EnterpriseDocumentAssessment', { state: { assessmentId: newId, analyzing: true } });
      } else {
        navigate(`/EnterpriseAssessment/${newId}?step=review`);
      }
    } catch (error) {
      setReassessError(error?.response?.data?.message || 'Could not reassess this assessment.');
    } finally {
      setReassessing(false);
    }
  };

  const fetchData = async () => {
    try {
      const org = await apiCall("GET", `/enterpriseOrganizations/details`);

      setOrgData({
        organizationId: org.organizationId,
        organizationName: org.organizationName,
        industry: org.industry,
        organizationAddress1: org.organizationAddress1,
        regionId: org.regionId,
        verticalId: org.verticalId,
        regionName: org.regionName,
      });

      const rawAssessments = org.assessments || [];
      const enriched = rawAssessments.map((a) => {
        const { riskCounts, riskScore } = computeRiskData(a.riskCounts);
        return {
          ...a,
          riskCounts,
          // total gates the View Risks button; activeRisks (non-completed / non-closed) is the label.
          totalRisks: a.riskCount ?? 0,
          activeRisks: a.activeRisks ?? 0,
          riskScore,
        };
      });

      setAssessments(enriched);
    } catch (err) {} finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!orgData) return null;

  return (
    <>
      <Card>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Organizations</h2>
        </div>

        <div className="space-y-3">
          {assessments.length === 0 ? (
            <div className="p-4 border-2 rounded-lg border-gray-200">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-medium text-content-primary">
                    {orgData.organizationName}
                  </h3>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500">No Assessment Data</div>
              <div className="flex flex-wrap gap-2 mt-3">
                {canManageRisks && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAssessmentForAssign({ organizationId: orgData.organizationId, hasExistingAssessment: false });
                      setShowAssignModal(true);
                    }}
                  >
                    <Shield className="h-3 w-3 mr-1" />
                    Assign
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!canAssess(null)}
                  title={getAssessDisabledReason(null) || ""}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowUnassignedAlert(true);
                  }}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Assess
                </Button>
                {canManageRisks && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="px-1 py-1 h-9 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/EnterpriseDocumentAssessment");
                    }}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Assess Document
                  </Button>
                )}
                <Button size="sm" variant="primary" className="px-1 py-1 h-9 text-xs" disabled>
                  <Shield className="h-4 w-4 mr-1" />
                  View Risks (0)
                </Button>
              </div>
            </div>
          ) : (
            assessments.map((assessment) => (
              <div
                key={assessment.enterpriseAssessmentID}
                className="p-4 border-2 rounded-lg border-gray-200 hover:border-gray-300 transition-colors"
              >
                <div className="flex gap-4 items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-content-primary">
                          {orgData.organizationName}
                          <AssessmentStatusBadge status={assessment.enterpriseStatus} />
                        </h3>
                        {assessment.enterpriseAssignedToUserName && (
                          <p className="text-xs text-blue-600 mt-1">
                            Assigned to: <span className="font-semibold">{assessment.enterpriseAssignedToUserName}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Risk details */}
                    {assessment.totalRisks > 0 ? (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2">
                          {assessment.riskCounts.CRITICAL > 0 && <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{assessment.riskCounts.CRITICAL} Critical</span>}
                          {assessment.riskCounts.HIGH > 0 && <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">{assessment.riskCounts.HIGH} High</span>}
                          {assessment.riskCounts.MEDIUM > 0 && <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">{assessment.riskCounts.MEDIUM} Medium</span>}
                          {assessment.riskCounts.LOW > 0 && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">{assessment.riskCounts.LOW} Low</span>}
                        </div>
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1">Risk Score: {assessment.riskScore || 0}%</div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div className={`h-3 rounded-full ${assessment.riskScore <= 40 ? "bg-green-500" : assessment.riskScore <= 60 ? "bg-yellow-500" : assessment.riskScore <= 80 ? "bg-orange-500" : "bg-red-500"}`} style={{ width: `${assessment.riskScore || 0}%` }} />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-gray-500">No Risk Data</div>
                    )}
                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 mt-3">
                  {canManageRisks && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={assessment.enterpriseStatus === "completed"}
                      title={assessment.enterpriseStatus === "completed" ? "Assessment is completed" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAssessmentForAssign({
                          organizationId: orgData.organizationId,
                          hasExistingAssessment: true,
                          enterpriseAssessmentID: assessment.enterpriseAssessmentID,
                        });
                        setShowAssignModal(true);
                      }}
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      {assessment.enterpriseAssignedToUserId ? "Reassign" : "Assign"}
                    </Button>
                  )}

                  {canManageRisks && assessment.enterpriseAssessmentID && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="whitespace-nowrap"
                      title="Manage additional respondents for this assessment"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRespondentsAssessmentId(assessment.enterpriseAssessmentID);
                      }}
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Respondents
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={
                      !canAssess(assessment.enterpriseAssignedToUserId, null, null, null, assessment.assigneeUserIds || []) ||
                      assessment.enterpriseStatus === "completed" ||
                      assessment.enterpriseStatus === "analyzing"
                    }
                    title={
                      assessment.enterpriseStatus === "completed" ? "Assessment is completed"
                        : assessment.enterpriseStatus === "analyzing" ? "Assessment is being analyzed"
                        : getAssessDisabledReason(assessment.enterpriseAssignedToUserId, null, null, null, assessment.assigneeUserIds || [])
                          || ""
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!assessment.enterpriseAssignedToUserId) {
                        setShowUnassignedAlert(true);
                        return;
                      }
                      navigate(`/EnterpriseAssessment/${assessment.enterpriseAssessmentID}`, {
                        state: {
                          organizationId: orgData.organizationId,
                          organizationName: orgData.organizationName,
                          regionId: orgData.regionId,
                          verticalId: orgData.verticalId,
                        },
                      });
                    }}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Assess
                  </Button>

                  {/* hasAnswers: nothing to show without answered questions (e.g. document-derived
                      assessments). Viewing mirrors the backend's GetViewableAssessmentAsync: org
                      read or assignment. */}
                  {assessment.enterpriseAssessmentID && assessment.enterpriseStatus === "completed" && assessment.hasAnswers &&
                    (canViewOrganizationAssessments() || canAssessViaAssignment(assessment.enterpriseAssignedToUserId, assessment.assigneeUserIds || [])) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      title="View the completed assessment's questions, answers, notes, and attachments (read-only)"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/EnterpriseAssessment/${assessment.enterpriseAssessmentID}?step=review`);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Answers
                    </Button>
                  )}

                  {canManageRisks && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="px-1 py-1 h-9 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/EnterpriseDocumentAssessment");
                      }}
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      Assess Document
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="primary"
                    className="px-1 py-1 h-9 text-xs"
                    // The round whose risks are live: the previous one while a reassessment is unfinished.
                    disabled={String(assessment.riskAssessmentStatus ?? assessment.enterpriseStatus ?? '').toLowerCase() !== 'completed'}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/EnterpriseAssessments/${assessment.riskAssessmentId ?? assessment.enterpriseAssessmentID}/risks`
                      );
                    }}
                  >
                    <Shield className="h-4 w-4 mr-1" />
                    {assessment.totalRisks === 0
                      ? 'View Results'
                      : `View Risks (${assessment.activeRisks ?? assessment.totalRisks})`}
                  </Button>

                  <EnterpriseAssessmentHistoryMenu
                    assessmentId={assessment.enterpriseAssessmentID}
                    hasHistory={assessment.hasHistory} />

                  {canManageOrganizationRisks() && assessment.enterpriseAssessmentID && (
                    <Button size="sm" variant="warning" className="px-1 py-1 h-9 text-xs"
                      disabled={assessment.enterpriseStatus !== 'completed'}
                      onClick={(e) => {
                        e.stopPropagation();
                        // isDocument drives the modal's wording only (the landing page comes back from the
                        // server). A document id alone doesn't make it a document assessment — an
                        // upload merged into this scope stamps one onto a questionnaire assessment
                        // too — so it must also have no answers to carry forward.
                        setAssessmentToReassess({
                          id: assessment.enterpriseAssessmentID,
                          isDocument: !!assessment.enterpriseAssessmentDocumentId && !assessment.hasAnswers,
                        });
                        setShowReassessModal(true);
                      }}>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Reassess
                    </Button>
                  )}

                  {canDeleteAssessment && assessment.enterpriseAssessmentID && (
                    <Button
                      size="sm"
                      variant="danger"
                      className="px-1 py-1 h-9 text-xs"
                      disabled={assessment.enterpriseStatus === "analyzing"}
                      title={assessment.enterpriseStatus === "analyzing" ? "Assessment is being analyzed" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteError(null);
                        setAssessmentToDelete(assessment.enterpriseAssessmentID);
                        setShowDeleteModal(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  )}
                    </div>
                    <ReassessmentNote status={assessment.reassessmentStatus} />
                  </div>
                  <div className="w-px bg-gray-200 self-stretch flex-shrink-0 mx-1"></div>
                  <div className="w-80 flex-shrink-0 bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Activity</p>
                    <AssessmentActivityLog assessmentType="enterprise" assessmentId={String(assessment.enterpriseAssessmentID)} hideQuestionEvents includeChain />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      <EnterpriseDeletedAssessments level="org" refreshKey={deletedVersion} />

      {showAssignModal && selectedAssessmentForAssign && (
        <OrgAssignAssessModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          organizationId={selectedAssessmentForAssign.organizationId}
          assessmentType="enterprise"
          hasExistingAssessment={selectedAssessmentForAssign.hasExistingAssessment}
          refreshData={fetchData}
        />
      )}

      <AssessmentRespondentsModal
        isOpen={respondentsAssessmentId != null}
        onClose={() => setRespondentsAssessmentId(null)}
        assessmentType="enterprise"
        assessmentId={respondentsAssessmentId}
        refreshData={fetchData}
      />

      <DeleteAssessmentModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setAssessmentToDelete(null); setDeleteError(null); }}
        onConfirm={handleDeleteAssessment}
        isDeleting={deleting}
        error={deleteError}
      />

      <ReassessModal
        isOpen={showReassessModal}
        onClose={() => { setShowReassessModal(false); setAssessmentToReassess(null); setReassessError(null); }}
        onConfirm={handleReassessAssessment}
        isReassessing={reassessing}
        isDocumentAssessment={assessmentToReassess?.isDocument}
        errorMessage={reassessError}
        previousStaysLive
      />

      <AssessmentUnassignedModal
        isOpen={showUnassignedAlert}
        onClose={() => setShowUnassignedAlert(false)}
      />
    </>
  );
};

export default EnterpriseOrganizationAssessmentCard;
